import React, { createContext, useContext, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import authService from '../api/services/authService';
import { refresh as refreshFieldRegistry, clearRegistryCache as clearFieldRegistry } from '../registry/fieldRegistry';

const AuthContext = createContext();

/**
 * Set Sentry user context
 * @param {Object|null} userData - User data or null to clear context
 */
const setSentryUser = (userData) => {
  if (userData) {
    Sentry.setUser({
      id: userData.id,
      email: userData.email,
      username: userData.fullName || `${userData.firstName} ${userData.lastName}`.trim(),
      role: userData.role,
      organizationId: userData.organizationId,
    });

    // Set additional context
    Sentry.setContext('user_details', {
      firstName: userData.firstName,
      lastName: userData.lastName,
      userRole: userData.userRole,
      hasProfilePicture: !!userData.profilePicture,
    });
  } else {
    Sentry.setUser(null);
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing token on app start
  useEffect(() => {
    const initializeAuth = async () => {
      const savedToken = authService.getToken();
      const savedUser = authService.getUserData();

      if (savedToken && savedUser) {
        setToken(savedToken);
        setUser(savedUser);

        // Refresh the field type registry in the background — non-blocking,
        // bundled fallback covers the brief window before fetch resolves.
        refreshFieldRegistry().catch(() => {});

        // Optional: Verify token is still valid by fetching current user
        try {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          setSentryUser(currentUser);
          localStorage.setItem('userData', JSON.stringify(currentUser));
        } catch (error) {
          // Token is invalid, clear auth state
          console.error('Token validation failed:', error);
          await authService.logout();
          setUser(null);
          setToken(null);
          setSentryUser(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{success: boolean, user?: Object, token?: string, error?: string}>}
   */
  const login = async (email, password) => {
    try {
      // Detect browser timezone for auto-set on first login
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Call real API endpoint
      const response = await authService.emailLogin({ email, password, browserTimezone });

      // Handle different possible response structures from the server
      // The API response might be wrapped in a 'data' property or returned directly
      const responseData = response.data || response;

      // Extract token - server might return 'token' or 'accessToken'
      const accessToken = responseData.token || responseData.accessToken;
      const refreshToken = responseData.refreshToken;

      // Extract user data - might be in 'user' property or at root level
      let userData = responseData.user || responseData;

      // If user data doesn't have required fields, construct from response
      if (!userData.email && responseData.email) {
        const extractedRole = responseData.role !== undefined ? responseData.role : responseData.userRole !== undefined ? responseData.userRole : 2;

        userData = {
          id: responseData.id || responseData.userId,
          email: responseData.email,
          firstName: responseData.firstName,
          lastName: responseData.lastName,
          fullName: responseData.fullName || `${responseData.firstName || ''} ${responseData.lastName || ''}`.trim(),
          role: extractedRole,
          userRole: extractedRole, // Store in both fields for compatibility
          organizationId: responseData.organizationId,
          profilePicture: responseData.profilePicture,
          // Read-only demo viewer flag — preserve through the flat-response reconstruction.
          isReadOnlyDemo: responseData.isReadOnlyDemo ?? userData.isReadOnlyDemo,
          // Demo org flag (builder + viewer) — drives the top-nav DEMO badge.
          isDemoOrg: responseData.isDemoOrg ?? userData.isDemoOrg,
        };
      } else {
        // User data exists, ensure role is set
        if (userData.role === undefined && userData.userRole !== undefined) {
          userData.role = userData.userRole;
        } else if (userData.userRole === undefined && userData.role !== undefined) {
          userData.userRole = userData.role;
        }
      }

      // Validate we have required data
      if (!accessToken) {
        throw new Error('No authentication token received from server');
      }

      // Store authentication data
      authService.storeAuthData(accessToken, refreshToken, userData);

      // Update state
      setUser(userData);
      setToken(accessToken);
      setSentryUser(userData);

      return { success: true, user: userData, token: accessToken };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message || 'Login failed. Please try again.'
      };
    }
  };

  /**
   * Register new user
   * @param {Object} userData - Registration data (email, password, firstName, lastName)
   * @returns {Promise<{success: boolean, user?: Object, token?: string, error?: string}>}
   */
  const register = async (userData) => {
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await authService.register({ ...userData, browserTimezone });

      // Extract data from response
      const { token: accessToken, refreshToken, user: newUser } = response;

      // Store authentication data
      authService.storeAuthData(accessToken, refreshToken, newUser);

      // Update state
      setUser(newUser);
      setToken(accessToken);
      setSentryUser(newUser);

      return { success: true, user: newUser, token: accessToken };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.message || 'Registration failed. Please try again.'
      };
    }
  };

  /**
   * Login with Google OAuth
   * @param {Object} googleData - Google OAuth data
   * @returns {Promise<{success: boolean, user?: Object, token?: string, error?: string}>}
   */
  const googleLogin = async (googleData) => {
    try {
      // Include browser timezone for auto-set on first login
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await authService.googleLogin({ ...googleData, browserTimezone });

      // Extract data from response
      const { token: accessToken, refreshToken, user: userData } = response;

      // Store authentication data
      authService.storeAuthData(accessToken, refreshToken, userData);

      // Update state
      setUser(userData);
      setToken(accessToken);
      setSentryUser(userData);

      return { success: true, user: userData, token: accessToken };
    } catch (error) {
      console.error('Google login error:', error);
      return {
        success: false,
        error: error.message || 'Google login failed. Please try again.'
      };
    }
  };

  /**
   * Logout user
   * Revokes refresh token on server and clears local state
   */
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local cleanup even if server revocation fails
    }
    setUser(null);
    setToken(null);
    setSentryUser(null);
    clearFieldRegistry();
  };

  /**
   * Update user data in state and localStorage
   * @param {Object} updatedUserData - Updated user data
   */
  const updateUser = (updatedUserData) => {
    const updatedUser = { ...user, ...updatedUserData };
    setUser(updatedUser);
    setSentryUser(updatedUser);

    // Update localStorage
    localStorage.setItem('userData', JSON.stringify(updatedUser));
    if (updatedUser.fullName) {
      localStorage.setItem('fullName', updatedUser.fullName);
    }
    if (updatedUser.role) {
      localStorage.setItem('role', updatedUser.role);
    }
  };

  /**
   * Refresh current user data from server
   */
  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      setSentryUser(currentUser);
      localStorage.setItem('userData', JSON.stringify(currentUser));
      return { success: true, user: currentUser };
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return { success: false, error: error.message };
    }
  };

  const isAuthenticated = !!token && !!user;

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    register,
    googleLogin,
    logout,
    updateUser,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};